import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7ca0b20b-418e-44ac-a9da-3db548a8f509/artifacts/eb1jluni_antropia_logo_voice_measurement_symbol_and_text_1775847083138.png";

const navLinks = [
  { href: "#problema", label: "Problema" },
  { href: "#como-funciona", label: "Cómo Funciona" },
  { href: "#funcionalidades", label: "Funcionalidades" },
  { href: "#precios", label: "Precios" },
  { href: "#faq", label: "FAQ" },
];

export const Header = ({ onOpenModal }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="header-logo">
              <img 
                src={LOGO_URL} 
                alt="Antropia" 
                className="h-10 md:h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-zinc-400 hover:text-white transition-colors text-sm font-medium"
                  data-testid={`nav-${link.href.replace("#", "")}`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-4">
              <button
                onClick={onOpenModal}
                className="bg-[#E05D3A] text-white hover:bg-[#C85233] transition-all shadow-[0_0_20px_rgba(224,93,58,0.3)] hover:shadow-[0_0_30px_rgba(224,93,58,0.5)] rounded-full px-6 py-2.5 text-sm font-medium"
                data-testid="header-cta-button"
              >
                Comenzar gratis
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl pt-24 lg:hidden"
          >
            <nav className="flex flex-col items-center gap-6 p-6">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-xl text-zinc-300 hover:text-white transition-colors"
                  data-testid={`mobile-nav-${link.href.replace("#", "")}`}
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onOpenModal();
                }}
                className="mt-4 bg-[#E05D3A] text-white hover:bg-[#C85233] transition-all shadow-[0_0_20px_rgba(224,93,58,0.3)] rounded-full px-8 py-3 text-base font-medium"
                data-testid="mobile-cta-button"
              >
                Comenzar gratis
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
